import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { messageAPI, profileAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Send, ArrowLeft, User } from 'lucide-react';

const MessagesPage = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [partner, setPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchMessages();
      fetchPartner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await messageAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await messageAPI.list();
      const filtered = response.data.filter(
        m => m.sender_id === userId || m.recipient_id === userId
      );
      setMessages(filtered.reverse());
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPartner = async () => {
    try {
      const response = await profileAPI.getProfile(userId);
      setPartner(response.data);
    } catch (error) {
      console.error('Error fetching partner:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !userId) return;

    setSending(true);
    try {
      await messageAPI.send({
        recipient_id: userId,
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('toasts.nachrichtFehler'));
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="h-[calc(100vh-8rem)]" data-testid="messages-page">
        <div className="grid md:grid-cols-3 gap-6 h-full">
          {/* Conversations List */}
          <Card className={`${userId ? 'hidden md:block' : ''}`}>
            <div className="p-4 border-b border-warm-200">
              <h2 className="font-semibold text-gray-900">Nachrichten</h2>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-14rem)]">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Laden...</div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Noch keine Nachrichten</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <Link
                    key={conv.partner_id}
                    to={`/nachrichten/${conv.partner_id}`}
                    className={`block p-4 hover:bg-warm-100 transition-colors border-b border-warm-100 ${
                      userId === conv.partner_id ? 'bg-warm-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 truncate">
                            {conv.partner_name}
                          </p>
                          {conv.unread_count > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {conv.last_message}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </Card>

          {/* Chat View */}
          <Card className="md:col-span-2">
            {userId ? (
              <div className="flex flex-col h-full">
                {/* Chat Header */}
                <div className="p-4 border-b border-warm-200 flex items-center gap-3">
                  <Link
                    to="/nachrichten"
                    className="md:hidden p-2 rounded-lg hover:bg-warm-100"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Link>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {partner?.company_name || partner?.user_name || t('toasts.laden')}
                    </p>
                    {partner?.bundesland && (
                      <p className="text-sm text-gray-500">{partner.bundesland}</p>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender_id === user?.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.sender_id === user?.id
                            ? 'bg-primary text-white rounded-br-sm'
                            : 'bg-warm-200 text-gray-900 rounded-bl-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user?.id ? 'text-white/70' : 'text-gray-500'
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString('de-DE', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSend} className="p-4 border-t border-warm-200">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={t('placeholders.nachrichtSchreiben')}
                      className="flex-1"
                      disabled={sending}
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-primary hover:bg-primary-600"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Wählen Sie eine Unterhaltung
                  </h3>
                  <p className="text-gray-500">
                    Oder starten Sie eine neue Unterhaltung über ein Projekt oder Anbieterprofil
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default MessagesPage;
